import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Save, X, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    short_description: '',
    full_description: '',
    features: [],
    order: 0
  });
  const [featuresText, setFeaturesText] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/products`, { withCredentials: true });
      setProducts(data);
    } catch (e) {
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      slug: product.slug,
      short_description: product.short_description,
      full_description: product.full_description || '',
      features: product.features || [],
      order: product.order || 0
    });
    setFeaturesText((product.features || []).join('\n'));
    setEditDialog(true);
  };

  const handleCreate = () => {
    setSelectedProduct(null);
    setFormData({
      name: '',
      slug: '',
      short_description: '',
      full_description: '',
      features: [],
      order: products.length
    });
    setFeaturesText('');
    setEditDialog(true);
  };

  const handleDelete = (product) => {
    setSelectedProduct(product);
    setDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/api/admin/products/${selectedProduct.id}`, { withCredentials: true });
      toast.success('Produit supprimé');
      fetchProducts();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialog(false);
      setSelectedProduct(null);
    }
  };

  const handleSave = async () => {
    const features = featuresText.split('\n').filter(f => f.trim());
    const payload = { ...formData, features };

    try {
      if (selectedProduct) {
        await axios.put(`${API}/api/admin/products/${selectedProduct.id}`, payload, { withCredentials: true });
        toast.success('Produit mis à jour');
      } else {
        await axios.post(`${API}/api/admin/products`, payload, { withCredentials: true });
        toast.success('Produit créé');
      }
      fetchProducts();
      setEditDialog(false);
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const { data } = await axios.post(`${API}/api/admin/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const updateData = type === 'image' ? { image_url: `${API}${data.url}` } : { pdf_url: `${API}${data.url}` };
      
      if (selectedProduct) {
        await axios.put(`${API}/api/admin/products/${selectedProduct.id}`, updateData, { withCredentials: true });
        toast.success(`${type === 'image' ? 'Image' : 'PDF'} uploadé`);
        fetchProducts();
      }
    } catch (e) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white h-20 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 mb-1">Produits</h1>
          <p className="text-slate-600">Gérez les produits du catalogue</p>
        </div>
        <Button onClick={handleCreate} className="bg-navy hover:bg-navy/90 text-white rounded-sm" data-testid="create-product-btn">
          <Plus className="h-4 w-4 mr-2" />
          Ajouter un produit
        </Button>
      </div>

      {/* Products List */}
      <div className="bg-white border border-slate-200">
        <div className="divide-y divide-slate-100">
          {products.map((product) => (
            <div key={product.id} className="p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6" data-testid={`product-row-${product.id}`}>
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-100 flex-shrink-0 overflow-hidden">
                {product.image_url ? (
                  <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                    No image
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-1">{product.name}</h3>
                <p className="text-slate-600 text-sm line-clamp-1">{product.short_description}</p>
                <div className="flex items-center gap-4 mt-2">
                  <span className="text-xs text-slate-500">Slug: {product.slug}</span>
                  {product.pdf_url && <span className="text-xs text-rose-600">PDF disponible</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 sm:flex-shrink-0 self-end sm:self-auto">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(product)} data-testid={`edit-product-${product.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={() => handleDelete(product)} data-testid={`delete-product-${product.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          {products.length === 0 && (
            <div className="p-12 text-center text-slate-500">
              Aucun produit. Cliquez sur "Ajouter un produit" pour commencer.
            </div>
          )}
        </div>
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedProduct ? 'Modifier le produit' : 'Nouveau produit'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Nom *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (!selectedProduct) {
                      setFormData(prev => ({ ...prev, name: e.target.value, slug: generateSlug(e.target.value) }));
                    }
                  }}
                  className="mt-1"
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <Label>Slug *</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="mt-1"
                  data-testid="product-slug-input"
                />
              </div>
            </div>

            <div>
              <Label>Description courte *</Label>
              <Textarea
                value={formData.short_description}
                onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                rows={2}
                className="mt-1"
                data-testid="product-short-desc-input"
              />
            </div>

            <div>
              <Label>Description complète</Label>
              <Textarea
                value={formData.full_description}
                onChange={(e) => setFormData({ ...formData, full_description: e.target.value })}
                rows={4}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Caractéristiques (une par ligne)</Label>
              <Textarea
                value={featuresText}
                onChange={(e) => setFeaturesText(e.target.value)}
                rows={4}
                placeholder="Portées jusqu'à 18m&#10;Isolation acoustique&#10;Rapidité de pose"
                className="mt-1"
              />
            </div>

            {selectedProduct && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Image</Label>
                  <div className="mt-1">
                    <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 cursor-pointer hover:border-cyan-500 transition-colors">
                      <Upload className="h-5 w-5 text-slate-400" />
                      <span className="text-sm text-slate-600">
                        {uploading ? 'Upload...' : 'Choisir une image'}
                      </span>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'image')} />
                    </label>
                  </div>
                </div>
                <div>
                  <Label>Fiche technique PDF</Label>
                  <div className="mt-1">
                    <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 cursor-pointer hover:border-rose-500 transition-colors">
                      <Upload className="h-5 w-5 text-slate-400" />
                      <span className="text-sm text-slate-600">
                        {uploading ? 'Upload...' : 'Choisir un PDF'}
                      </span>
                      <input type="file" accept=".pdf" className="hidden" onChange={(e) => handleFileUpload(e, 'pdf')} />
                    </label>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                <X className="h-4 w-4 mr-2" />
                Annuler
              </Button>
              <Button onClick={handleSave} className="bg-navy hover:bg-navy/90 text-white" data-testid="save-product-btn">
                <Save className="h-4 w-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce produit ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le produit "{selectedProduct?.name}" sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminProducts;
